/**
 * Espelho da base: a máquina dos blocos guarda também as imagens INTEIRAS de
 * 450/900/1800 px que o STAR publica — o mesmo arquivo, byte a byte —, em
 * `/blocos/v2/nsa/<produto>/<carimbo>/<largura>.jpg`.
 *
 * Medido (02/10/2026): o STAR não tem CDN na frente; o primeiro byte leva
 * 140–260 ms, e 1% dos pedidos passa de 1 s (até 11 s) — num loop de 17 quadros,
 * ~16% das vezes algum quadro trava a reprodução. O espelho responde em ~50 ms
 * (p99 0,2 s), na mesma conexão HTTP/2 dos blocos, com o dobro da vazão.
 *
 * Só o download muda de endereço (goes/frameCache.ts, `definirEspelho`): a
 * chave do cache e o depósito em disco seguem com a URL do STAR. 404 (quadro
 * que o espelho ainda não tem), 5xx, rede ou demora: aquele pedido vai ao STAR.
 *
 * Só com o portal parado antes do primeiro play (`loopLoadAllowed` falso: a
 * abertura e o quadro mais recente), onde o que conta é o primeiro byte —
 * medido em 4G, 1º pixel em 2,9 s contra 4,3 s pelo STAR. O loop, que é volume,
 * segue do STAR: com tudo numa conexão só até a máquina dos blocos, a 1ª volta
 * do play no z6 levou 60 s contra 36 s com a base do STAR e os blocos da
 * máquina em caminhos separados (5 repetições alternadas, 02/10 à noite).
 *
 * Três falhas seguidas (rede, 5xx ou demora; 404 não conta) e o espelho fica de
 * lado por 5 min: cada pedido pagaria a espera antes de ir ao STAR. Falhas de
 * rede e 5xx contam para a saúde dos blocos como as dos blocos; DEMORA não —
 * medido (02/10, 18:48): com a máquina respondendo em 4–10 s, os prazos do
 * espelho derrubavam os blocos e o satélite voltava ao arquivo de 7200
 * (117 MB num loop de 3 h).
 */
import { definirEspelho } from '../goes/frameCache';
import { STAR_SECTOR_URL } from '../goes/star';
import { HttpError, TimeoutError } from '../lib/fetchRetry';
import { loopLoadAllowed } from '../loop/store';
import { BLOCOS_URL, VERSAO_BLOCOS } from './blocos';
import { registrarBloco, useBlocosStore } from './saude';

const ARQUIVO_DO_STAR = /^\/([^/]+)\/(\d{11})_GOES19-ABI-nsa-[^/]+-(\d+)x\d+\.jpg$/;

/**
 * Endereço no espelho de um arquivo inteiro do STAR, ou null se não for um
 * arquivo do setor, a largura não for espelhada ou não houver servidor. Puro — testado.
 */
export function urlDoEspelho(url: string, inteiros: readonly number[], blocosUrl: string = BLOCOS_URL): string | null {
  if (!blocosUrl || !inteiros.length || !url.startsWith(STAR_SECTOR_URL)) return null;
  const m = ARQUIVO_DO_STAR.exec(url.slice(STAR_SECTOR_URL.length));
  if (!m || !inteiros.includes(Number(m[3]))) return null;
  return `${blocosUrl}/${VERSAO_BLOCOS}/nsa/${m[1]}/${m[2]}/${m[3]}.jpg`;
}

/**
 * Larguras presumidas enquanto a primeira verificação (`/saude`) não volta: a
 * base da abertura já sai do espelho. Se o servidor não as tiver, é um 404
 * rápido e o pedido vai ao STAR.
 */
export const INTEIROS_PRESUMIDOS: readonly number[] = [450, 900, 1800];

/** Larguras a pedir ao espelho agora (nenhuma com o servidor fora). Puro — testado. */
export function inteirosEmUso(estado: string, inteiros: readonly number[]): readonly number[] {
  if (estado === 'ok') return inteiros;
  return estado === 'verificando' ? INTEIROS_PRESUMIDOS : [];
}

/** Falhas seguidas que põem o espelho de lado, e por quanto tempo. */
export const FALHAS_PARA_PAUSAR = 3;
export const PAUSA_MS = 5 * 60_000;

/**
 * Disjuntor do espelho: conta falhas seguidas (404 não é falha: o quadro só
 * não chegou lá ainda) e diz até quando ele fica de lado. Puro — testado.
 */
export function criarDisjuntor(agora: () => number = () => Date.now()) {
  let seguidas = 0;
  let pausadoAte = 0;
  return {
    aberto: () => agora() >= pausadoAte,
    registrar(erro: unknown | null): void {
      if (erro === null || (erro instanceof HttpError && erro.status < 500)) {
        if (erro === null) seguidas = 0;
        return;
      }
      if (++seguidas >= FALHAS_PARA_PAUSAR) {
        seguidas = 0;
        pausadoAte = agora() + PAUSA_MS;
      }
    },
  };
}

/** Liga o espelho nos downloads do satélite (vale enquanto o servidor de blocos está no ar). */
export function ligarEspelho(): () => void {
  const disjuntor = criarDisjuntor();
  definirEspelho({
    endereco: (url) => {
      if (!disjuntor.aberto() || loopLoadAllowed()) return null;
      const { estado, inteiros } = useBlocosStore.getState();
      return urlDoEspelho(url, inteirosEmUso(estado, inteiros));
    },
    registrar: (erro) => {
      disjuntor.registrar(erro);
      // Demora não é servidor fora do ar (e o pedido já foi ao STAR).
      if (!(erro instanceof TimeoutError)) registrarBloco(erro);
    },
  });
  return () => definirEspelho(null);
}
